import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import * as S from "./styles";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

function ExcelViewer({ filePath, fileName }) {
  const [sheets, setSheets] = useState([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!filePath) return;

    const xhr = new XMLHttpRequest();
    xhr.open("GET", filePath);
    xhr.responseType = "arraybuffer";

    xhr.onload = () => {
      if (xhr.status === 200) {
        try {
          const arrayBuffer = xhr.response;
          const workbook = XLSX.read(arrayBuffer, { type: "array" });

          function readSheetManual(sheet) {
            const range = XLSX.utils.decode_range(sheet["!ref"]);
            const data = [];

            for (let R = range.s.r; R <= range.e.r; ++R) {
              const row = [];
              for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellAddress = { r: R, c: C };
                const cellRef = XLSX.utils.encode_cell(cellAddress);
                const cell = sheet[cellRef];
                if (cell) {
                  row.push(cell.w ?? cell.v ?? "");
                } else {
                  row.push("");
                }
              }
              data.push(row);
            }

            // Pega merges da planilha (array de merges)
            const merges = sheet["!merges"] || [];

            return { data, merges };
          }

          const allSheets = workbook.SheetNames.map((sheetName) => {
            const sheet = workbook.Sheets[sheetName];
            const { data, merges } = readSheetManual(sheet);
            return { nome: sheetName, dados: data, merges };
          });

          if (allSheets.length === 0) {
            setErro("Nenhuma planilha encontrada no arquivo.");
            return;
          }

          setSheets(allSheets);
          setActiveSheetIndex(0);
          setErro("");
        } catch (e) {
          setErro("Erro ao processar arquivo Excel");
          console.error(e);
        }
      } else {
        setErro(`Falha ao carregar arquivo: status ${xhr.status}`);
      }
    };

    xhr.onerror = () => setErro("Erro ao carregar arquivo Excel");

    xhr.send();
  }, [filePath]);

  if (erro)
    return (
      <p style={{ color: "red", textAlign: "center", marginTop: 30 }}>{erro}</p>
    );
  if (sheets.length === 0)
    return <p style={{ textAlign: "center", marginTop: 30 }}>Carregando planilhas...</p>;

  // Aba ativa
  const { dados, merges } = sheets[activeSheetIndex];
  const colunas = dados[0] || [];
  const linhas = dados.slice(1);

  // Função para tentar interpretar valor como data válida
  function tryParseDate(value) {
    if (!value) return null;
    const parsed = Date.parse(value);
    if (!isNaN(parsed)) return new Date(parsed);
    return null;
  }

const linhasFormatadas = linhas;
const colunasFormatadas = colunas;

  // Cria mapa para controle dos merges
  const mergeMap = {};

  merges.forEach(({ s, e }) => {
    const rowSpan = e.r - s.r + 1;
    const colSpan = e.c - s.c + 1;

    // Raiz do merge
    mergeMap[`${s.r},${s.c}`] = { rowSpan, colSpan, isRoot: true };

    // Células cobertas (ignorar na renderização)
    for (let r = s.r; r <= e.r; r++) {
      for (let c = s.c; c <= e.c; c++) {
        if (r === s.r && c === s.c) continue;
        mergeMap[`${r},${c}`] = { isRoot: false };
      }
    }
  });

  return (
    <S.Container>
      <S.DownloadLink href={filePath} download title="Baixar arquivo Excel">
        ⬇️ Download Arquivo - {fileName}
      </S.DownloadLink>

      <S.TabsWrapper>
        {sheets.map((sheet, i) => (
          <S.Tab
            key={sheet.nome}
            active={i === activeSheetIndex}
            onClick={() => setActiveSheetIndex(i)}
          >
            {sheet.nome}
          </S.Tab>
        ))}
      </S.TabsWrapper>

      <S.Table>
        <thead>
          <tr>
            {colunasFormatadas.map((col, i) => (
              <S.Th key={i}>{col}</S.Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhasFormatadas.map((linha, rowIndex) => (
            <S.Tr key={rowIndex} index={rowIndex}>
              {linha.map((celula, colIndex) => {
                const key = `${rowIndex + 1},${colIndex}`; 
                // +1 no rowIndex porque linhasFormatadas começa depois do cabeçalho
                const mergeInfo = mergeMap[key];

                if (mergeInfo && !mergeInfo.isRoot) return null;

                return (
                  <S.Td
                    key={colIndex}
                    rowSpan={mergeInfo?.rowSpan || 1}
                    colSpan={mergeInfo?.colSpan || 1}
                  >
                    {celula}
                  </S.Td>
                );
              })}
            </S.Tr>
          ))}
        </tbody>
      </S.Table>
    </S.Container>
  );
}

export default ExcelViewer;
